import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, Award, AlertTriangle, RotateCcw, Upload, Calendar, ShieldCheck, History } from 'lucide-react';
import CanAccess from '@/components/ui/CanAccess';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import ALCOABadge from '@/components/ui/ALCOABadge';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import { format, differenceInDays } from 'date-fns';

interface Cert {
  id: string;
  certCode: string;
  name: string;
  body: string;
  type: string;
  lab: { name: string; labCode: string };
  status: string;
  issueDate: string;
  expiryDate: string;
  daysLeft: number;
  nextAuditDate: string;
  scope: string;
  issuedBy?: string;
  verifiedBy?: string;
  createdBy?: { name: string; role: string };
  createdAt?: string;
  updatedAt?: string;
  // enriched fields
  certificateNo?: string;
  complianceScore?: number;
  renewalCount?: number;
  lastRenewalDate?: string;
  renewalHistory?: { date: string; renewedBy: string; notes: string }[];
  auditHistory?: { date: string; type: string; auditor: string; score: number; finding: string }[];
}

interface RenewalScheduleItem {
  id: string;
  name: string;
  lab: { name: string };
  expiryDate: string;
  status: string;
  daysLeft: number;
}

const MOCK_CERTS: Cert[] = [
  {
    id: 'mock-cert-1', certCode: 'CERT-NABL-001', name: 'NABL Accreditation — Chemical & Microbiology',
    body: 'National Accreditation Board for Testing & Calibration Laboratories', type: 'NABL',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, status: 'valid',
    issueDate: '2023-04-01', expiryDate: '2026-03-31', daysLeft: 354,
    nextAuditDate: '2025-09-15', scope: 'Chemical & Microbiological testing of pharmaceutical raw materials and finished products',
    issuedBy: 'Dr. Anita Sharma', verifiedBy: 'Rajesh Kumar',
  },
  {
    id: 'mock-cert-2', certCode: 'CERT-GMP-002', name: 'GMP Compliance Certificate — Formulation',
    body: 'Central Drugs Standard Control Organisation', type: 'GMP',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, status: 'valid',
    issueDate: '2023-07-10', expiryDate: '2026-07-09', daysLeft: 454,
    nextAuditDate: '2025-10-20', scope: 'Good Manufacturing Practice for oral solid dosage formulation — tablets and capsules',
    issuedBy: 'Dr. Meena Iyer', verifiedBy: 'Sunil Patel',
  },
  {
    id: 'mock-cert-3', certCode: 'CERT-ISO-003', name: 'ISO 17025:2017 — Analytical Testing',
    body: 'Bureau Veritas Certification India', type: 'ISO',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, status: 'renewal_due',
    issueDate: '2022-11-01', expiryDate: '2025-10-31', daysLeft: 23,
    nextAuditDate: '2025-05-10', scope: 'Competence of testing and calibration laboratories for API characterisation and identification',
    issuedBy: 'Harish Nair', verifiedBy: 'Dr. Priya Singh',
  },
  {
    id: 'mock-cert-4', certCode: 'CERT-WHO-004', name: 'WHO GMP Certificate — Sterile Manufacturing',
    body: 'World Health Organisation', type: 'WHO',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, status: 'valid',
    issueDate: '2024-01-15', expiryDate: '2027-01-14', daysLeft: 644,
    nextAuditDate: '2026-01-10', scope: 'Sterile manufacturing and aseptic fill-finish operations for injectables',
    issuedBy: 'Dr. Kavitha Reddy', verifiedBy: 'Arun Menon',
  },
  {
    id: 'mock-cert-5', certCode: 'CERT-ISO-005', name: 'ISO 9001:2015 — Quality Management System',
    body: 'TÜV SÜD South Asia', type: 'ISO',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, status: 'valid',
    issueDate: '2023-08-20', expiryDate: '2026-08-19', daysLeft: 495,
    nextAuditDate: '2025-11-05', scope: 'Quality management system for long-term and accelerated stability studies of pharmaceutical products',
    issuedBy: 'Neha Gupta', verifiedBy: 'Dr. Suresh Pillai',
  },
  {
    id: 'mock-cert-6', certCode: 'CERT-GMP-006', name: 'Schedule M GMP Licence',
    body: 'Central Drugs Standard Control Organisation', type: 'GMP',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, status: 'valid',
    issueDate: '2024-03-01', expiryDate: '2027-02-28', daysLeft: 688,
    nextAuditDate: '2026-03-15', scope: 'Revised Schedule M compliance for large-volume parenterals and small-volume injectables',
    issuedBy: 'Dr. Ravi Shankar', verifiedBy: 'Meenakshi Iyer',
  },
  {
    id: 'mock-cert-7', certCode: 'CERT-NABL-007', name: 'NABL Accreditation — Microbiological Testing',
    body: 'National Accreditation Board for Testing & Calibration Laboratories', type: 'NABL',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, status: 'valid',
    issueDate: '2024-06-01', expiryDate: '2027-05-31', daysLeft: 780,
    nextAuditDate: '2026-06-10', scope: 'Microbiological testing including sterility, endotoxin, and bioburden for formulated products',
    issuedBy: 'Dr. Anjali Mehta', verifiedBy: 'Vikram Desai',
  },
  {
    id: 'mock-cert-8', certCode: 'CERT-FDA-008', name: 'USFDA Establishment Registration',
    body: 'U.S. Food & Drug Administration', type: 'FDA',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, status: 'valid',
    issueDate: '2023-10-01', expiryDate: '2026-09-30', daysLeft: 537,
    nextAuditDate: '2025-12-01', scope: 'API manufacturing facility registration under 21 CFR Part 207 — synthetic active pharmaceutical ingredients',
    issuedBy: 'Dr. Sanjay Rao', verifiedBy: 'Priya Nair',
  },
  {
    id: 'mock-cert-9', certCode: 'CERT-EUGMP-009', name: 'EU GMP Certificate — API Manufacture',
    body: 'European Medicines Agency', type: 'EU GMP',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, status: 'renewal_due',
    issueDate: '2022-06-15', expiryDate: '2025-06-14', daysLeft: 18,
    nextAuditDate: '2025-04-30', scope: 'Good Manufacturing Practice for active pharmaceutical ingredients per EudraLex Volume 4 Part II',
    issuedBy: 'Hans-Dieter Koch', verifiedBy: 'Dr. Anita Sharma',
  },
  {
    id: 'mock-cert-10', certCode: 'CERT-ISO-010', name: 'ISO 14001:2015 — Environmental Management',
    body: 'Bureau Veritas Certification India', type: 'ISO',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, status: 'valid',
    issueDate: '2023-01-10', expiryDate: '2026-01-09', daysLeft: 273,
    nextAuditDate: '2025-07-20', scope: 'Environmental management system for chemical waste disposal, effluent treatment, and lab operations',
    issuedBy: 'Arun Menon', verifiedBy: 'Nandini Verma',
  },
  {
    id: 'mock-cert-11', certCode: 'CERT-WHO-011', name: 'WHO Prequalification — Sterile Products',
    body: 'World Health Organisation', type: 'WHO',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, status: 'expired',
    issueDate: '2021-03-01', expiryDate: '2024-02-29', daysLeft: -41,
    nextAuditDate: '2024-08-01', scope: 'WHO prequalification for sterile injectable products intended for international procurement programmes',
    issuedBy: 'Dr. Kavitha Reddy', verifiedBy: 'Arun Menon',
  },
  {
    id: 'mock-cert-12', certCode: 'CERT-GMP-012', name: 'GMP — Stability Chamber Operations',
    body: 'Central Drugs Standard Control Organisation', type: 'GMP',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, status: 'valid',
    issueDate: '2024-09-01', expiryDate: '2027-08-31', daysLeft: 871,
    nextAuditDate: '2026-09-01', scope: 'Compliance of accelerated and long-term stability chamber conditions per ICH Q1A(R2) guidelines',
    issuedBy: 'Dr. Suresh Pillai', verifiedBy: 'Ramesh Kulkarni',
  },
  {
    id: 'mock-cert-13', certCode: 'CERT-EUGMP-013', name: 'EU GMP Certificate — Finished Dosage',
    body: 'European Medicines Agency', type: 'EU GMP',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, status: 'pending',
    issueDate: '2025-01-01', expiryDate: '2028-12-31', daysLeft: 1359,
    nextAuditDate: '2025-06-15', scope: 'GMP compliance for oral solid dosage forms intended for EU market supply — tablets, capsules, sachets',
    issuedBy: '', verifiedBy: '',
  },
  {
    id: 'mock-cert-14', certCode: 'CERT-NABL-014', name: 'NABL Accreditation — Calibration Services',
    body: 'National Accreditation Board for Testing & Calibration Laboratories', type: 'NABL',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, status: 'renewal_due',
    issueDate: '2022-05-01', expiryDate: '2025-04-30', daysLeft: 12,
    nextAuditDate: '2025-03-15', scope: 'Calibration of temperature and humidity recorders, stability chambers, HPLC systems, and weighing balances',
    issuedBy: 'Vinod Sharma', verifiedBy: 'Dr. Suresh Pillai',
  },
  {
    id: 'mock-cert-15', certCode: 'CERT-USFDA-015', name: 'USFDA Drug Master File — Type II',
    body: 'U.S. Food & Drug Administration', type: 'USFDA',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, status: 'revoked',
    issueDate: '2020-08-01', expiryDate: '2023-07-31', daysLeft: -619,
    nextAuditDate: '2023-02-01', scope: 'Drug Master File for excipient manufacturing and quality systems — revoked due to data integrity findings',
    issuedBy: 'Dr. Anita Sharma', verifiedBy: 'Rajesh Kumar',
  },
  {
    id: 'mock-cert-16', certCode: 'CERT-CE-016', name: 'CE Mark — In-Vitro Diagnostic Equipment',
    body: 'TÜV SÜD South Asia', type: 'CE',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, status: 'expired',
    issueDate: '2021-11-01', expiryDate: '2024-10-31', daysLeft: -163,
    nextAuditDate: '2024-05-15', scope: 'CE marking for in-vitro diagnostic instruments and consumables used in QC testing workflows',
    issuedBy: 'Michael Brandt', verifiedBy: 'Nandini Verma',
  },
];

export default function Certifications() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [typeFilter, setTypeFilter] = useState('');
  const [labId, setLabId] = useState('');
  const [dateRange, setDateRange] = useState('6m');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Cert | null>(null);
  const [form, setForm] = useState({
    name: '', body: '', type: '', labId: '',
    issueDate: '', expiryDate: '', scope: '',
  });
  const queryClient = useQueryClient();

  // --- New state for renewal workflow ---
  const [showRenew, setShowRenew] = useState<string | null>(null); // cert id
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [renewForm, setRenewForm] = useState({
    renewalDate: '', renewedBy: '', renewalCost: '', notifyDays: '14', notes: '',
  });

  // --- New state for batch upload ---
  const [batchFiles, setBatchFiles] = useState<FileList | null>(null);
  const [batchLabId, setBatchLabId] = useState('');
  const [batchCertType, setBatchCertType] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['certs', { search, status: statusFilter, type: typeFilter, page, labId, dateRange }],
    queryFn: () =>
      api.get('/certifications', { params: { search, status: statusFilter, type: typeFilter, page, limit: 20, ...(labId && { labId }), range: dateRange } })
        .then((r) => r.data),
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['cert-health-chart'],
    queryFn: () => api.get('/certifications/health-chart').then((r) => r.data.data),
    staleTime: 300000,
  });

  const { data: timelineData } = useQuery({
    queryKey: ['cert-expiry-timeline'],
    queryFn: () => api.get('/certifications/expiry-timeline').then((r) => r.data.data),
    staleTime: 300000,
  });

  // Renewal schedule query
  const { data: renewalSchedule } = useQuery({
    queryKey: ['cert-renewal-schedule'],
    queryFn: () => api.get('/certifications/renewal-schedule').then(r => r.data.data).catch(() => []),
    staleTime: 300000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/certifications', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowCreate(false);
      setForm({ name: '', body: '', type: '', labId: '', issueDate: '', expiryDate: '', scope: '' });
    },
  });

  const renewMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: typeof renewForm }) =>
      api.post(`/certifications/${id}/renew`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certs'] });
      queryClient.invalidateQueries({ queryKey: ['cert-renewal-schedule'] });
      setShowRenew(null);
      setRenewForm({ renewalDate: '', renewedBy: '', renewalCost: '', notifyDays: '14', notes: '' });
    },
  });

  const batchUploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      if (batchFiles) {
        Array.from(batchFiles).forEach((f) => fd.append('files', f));
      }
      fd.append('labId', batchLabId);
      fd.append('certType', batchCertType);
      return api.post('/certifications/batch-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certs'] });
      setShowBatchUpload(false);
      setBatchFiles(null);
      setBatchLabId('');
      setBatchCertType('');
    },
  });

  const getDaysLeftColor = (days: number) => {
    if (days < 0) return 'text-risk-high font-semibold';
    if (days <= 30) return 'text-risk-high';
    if (days <= 90) return 'text-risk-medium';
    return 'text-risk-low';
  };

  // Group renewal schedule by month
  const renewalByMonth = React.useMemo(() => {
    if (!renewalSchedule || !Array.isArray(renewalSchedule)) return {};
    const grouped: Record<string, RenewalScheduleItem[]> = {};
    (renewalSchedule as RenewalScheduleItem[]).forEach((item) => {
      const monthKey = format(new Date(item.expiryDate), 'MMMM yyyy');
      if (!grouped[monthKey]) grouped[monthKey] = [];
      grouped[monthKey].push(item);
    });
    return grouped;
  }, [renewalSchedule]);

  // Auto-open detail when navigated from a notification (?openId=certN)
  const openId = searchParams.get('openId');
  const { data: openCertData } = useQuery({
    queryKey: ['cert-detail', openId],
    queryFn: () => api.get(`/certifications/${openId}`).then(r => r.data.data as Cert),
    enabled: !!openId,
  });
  useEffect(() => {
    if (openCertData) setSelectedCert(openCertData);
  }, [openCertData]);

  const renewingCert = showRenew ? (data?.data as Cert[] || []).find((c) => c.id === showRenew) : null;

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'certCode', label: 'Cert ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.certCode)}</span> },
    { key: 'name', label: 'Certification',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.name)}</p>
          <p className="text-xs text-text-dim">{String(row.body)} · {String(row.type)}</p>
        </div>
      )},
    { key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string; labCode: string } | null;
        return <span className="text-sm text-text">{lab?.name || '—'}</span>;
      }},
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'expiryDate', label: 'Expiry',
      render: (row) => {
        const daysLeft = Number(row.daysLeft);
        return (
          <div>
            <p className="text-xs font-mono">{format(new Date(String(row.expiryDate)), 'd MMM yyyy')}</p>
            <p className={`text-[10px] font-semibold ${getDaysLeftColor(daysLeft)}`}>
              {daysLeft < 0 ? `${Math.abs(daysLeft)}d expired` : `${daysLeft}d left`}
            </p>
          </div>
        );
      }},
    { key: 'nextAuditDate', label: 'Next Audit',
      render: (row) => (
        <span className="text-xs font-mono text-text-muted">
          {row.nextAuditDate ? format(new Date(String(row.nextAuditDate)), 'd MMM yyyy') : '—'}
        </span>
      )},
    { key: 'actions', label: '',
      render: (row) => {
        const status = String(row.status);
        if (status === 'renewal_due' || status === 'expired') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); setShowRenew(String(row.id)); }}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-risk-medium/10 text-risk-medium hover:bg-risk-medium/20 transition-colors font-medium"
            >
              <RotateCcw size={11} /> Start Renewal
            </button>
          );
        }
        return null;
      }},
  ];

  const handleExport = () => {
    const rows = data?.data as Cert[] || [];
    const csv = ['Code,Name,Body,Type,Lab,Status,Issue Date,Expiry Date,Days Left'].concat(
      rows.map((c) => `${c.certCode},"${c.name}",${c.body},${c.type},"${c.lab?.name}",${c.status},${c.issueDate},${c.expiryDate},${c.daysLeft}`)
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'certifications.csv'; a.click();
  };

  const HEALTH_COLORS: Record<string, string> = {
    valid: '#16A34A', renewal_due: '#D97706', expired: '#DC2626', suspended: '#6B7280',
  };

  const getRenewalStatusColor = (days: number, status: string) => {
    if (status === 'expired' || days < 0) return { dot: 'bg-risk-high', text: 'text-risk-high' };
    if (days <= 30) return { dot: 'bg-risk-high', text: 'text-risk-high' };
    if (days <= 90) return { dot: 'bg-risk-medium', text: 'text-risk-medium' };
    return { dot: 'bg-risk-low', text: 'text-risk-low' };
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Certifications"
        subtitle={`${data?.meta?.total || 0} certifications monitored`}
        actions={
          <>
            <ALCOABadge />
            <button onClick={handleExport} className="btn-secondary"><Download size={13} /> Export</button>
            <CanAccess roles={['admin', 'qa_director']}>
              <button onClick={() => setShowBatchUpload(true)} className="btn-secondary">
                <Upload size={13} /> Batch Upload
              </button>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> Add Cert</button>
            </CanAccess>
          </>
        }
      />

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        dateRange={dateRange} onDateRangeChange={(r) => { setDateRange(r); setPage(1); }}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {healthLoading ? <ChartSkeleton height={200} /> : (
          <ChartCard title="Certification Health" subtitle="Status summary across all labs" height={200}>
            <ResponsiveContainer>
              <BarChart data={Array.isArray(healthData) ? healthData : []} margin={{ top: 5, right: 20, bottom: 20, left: 0 }} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6899A0' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={30} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [v, 'Count']} />
                <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                  {(Array.isArray(healthData) ? healthData : []).map((entry: { color: string }, i: number) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard title="12-Month Expiry Timeline" subtitle="Certs expiring per month" height={200}>
          <ResponsiveContainer>
            <BarChart data={Array.isArray(timelineData) ? timelineData : []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6899A0' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={25} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" name="Expiring" radius={[3, 3, 0, 0]}>
                {(Array.isArray(timelineData) ? timelineData : []).map((entry: { isPast: boolean }, i: number) => (
                  <Cell key={i} fill={entry.isPast ? '#DC2626' : '#b07d1a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Alert for expiring soon */}
      {(data?.data as Cert[] || []).some((c) => c.daysLeft >= 0 && c.daysLeft <= 30) && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-risk-high/5 border border-risk-high/20">
          <AlertTriangle size={15} className="text-risk-high mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-risk-high">Certifications expiring within 30 days</p>
            <p className="text-xs text-text-muted mt-0.5">
              {(data?.data as Cert[] || []).filter((c) => c.daysLeft >= 0 && c.daysLeft <= 30).map((c) => c.name).join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Renewal Schedule Section */}
      {renewalSchedule && Array.isArray(renewalSchedule) && renewalSchedule.length > 0 && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-teal" />
            <h3 className="text-sm font-semibold text-text">Renewal Schedule</h3>
            <span className="text-xs text-text-muted">— upcoming certification renewals</span>
          </div>
          <div className="space-y-5">
            {Object.entries(renewalByMonth).map(([month, items]) => (
              <div key={month}>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Due in {month}</p>
                <div className="space-y-2 pl-2 border-l-2 border-border">
                  {items.map((item) => {
                    const colors = getRenewalStatusColor(item.daysLeft, item.status);
                    return (
                      <div key={item.id} className="flex items-center gap-3 py-1.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text truncate">{item.name}</p>
                          <p className="text-xs text-text-muted">{item.lab?.name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-xs font-semibold ${colors.text}`}>
                            {item.daysLeft < 0
                              ? `${Math.abs(item.daysLeft)}d overdue`
                              : item.daysLeft === 0
                              ? 'Due today'
                              : `${item.daysLeft}d left`}
                          </p>
                          <PillBadge value={item.status} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search certifications..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="valid">Valid</option>
          <option value="renewal_due">Renewal Due</option>
          <option value="expired">Expired</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Types</option>
          <option value="ISO">ISO</option>
          <option value="GMP">GMP</option>
          <option value="NABL">NABL</option>
          <option value="WHO">WHO</option>
          <option value="FDA">FDA</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={(data?.data ?? MOCK_CERTS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedCert(row as unknown as Cert)}
        total={data?.meta?.total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Create Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Certification"
        subtitle="Register a new certification" size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Adding...' : 'Add Certification'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Certification Name *</label>
            <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. ISO 9001:2015" />
          </div>
          <div>
            <label className="label">Certifying Body</label>
            <input className="input" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="e.g. Bureau Veritas" />
          </div>
          <div>
            <label className="label">Type</label>
            <select className="select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              <option value="">Select type...</option>
              {['ISO', 'GMP', 'NABL', 'WHO', 'FDA', 'Other'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Lab *</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {(labs || []).map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Issue Date *</label>
            <input type="date" className="input" value={form.issueDate} onChange={(e) => setForm((f) => ({ ...f, issueDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Expiry Date *</label>
            <input type="date" className="input" value={form.expiryDate} onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Scope</label>
            <textarea className="input resize-none" rows={2} value={form.scope}
              onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} placeholder="Certification scope..." />
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedCert} onClose={() => setSelectedCert(null)}
        title={selectedCert?.name || ''} subtitle={selectedCert?.certCode} size="lg">
        {selectedCert && (
          <div className="space-y-4">
            {/* Status + key metrics row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Status</p><PillBadge value={selectedCert.status} showDot /></div>
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Type</p><p className="text-sm font-medium text-text mt-0.5">{selectedCert.type}</p></div>
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Issued</p><p className="text-sm font-mono mt-0.5">{format(new Date(selectedCert.issueDate), 'd MMM yyyy')}</p></div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted">Expires</p>
                <p className={`text-sm font-mono mt-0.5 ${getDaysLeftColor(selectedCert.daysLeft)}`}>
                  {format(new Date(selectedCert.expiryDate), 'd MMM yyyy')}
                </p>
                <p className={`text-[10px] mt-0.5 ${getDaysLeftColor(selectedCert.daysLeft)}`}>
                  {selectedCert.daysLeft < 0 ? `${Math.abs(selectedCert.daysLeft)}d expired` : `${selectedCert.daysLeft}d remaining`}
                </p>
              </div>
            </div>

            {/* Certificate No + Compliance Score */}
            {(selectedCert.certificateNo || selectedCert.complianceScore !== undefined) && (
              <div className="grid grid-cols-2 gap-3">
                {selectedCert.certificateNo && (
                  <div className="p-3 bg-bg rounded-lg">
                    <p className="text-xs text-text-muted mb-1">Certificate No.</p>
                    <p className="text-xs font-mono font-semibold text-text">{selectedCert.certificateNo}</p>
                  </div>
                )}
                {selectedCert.complianceScore !== undefined && (
                  <div className="p-3 bg-bg rounded-lg">
                    <p className="text-xs text-text-muted mb-1">Compliance Score</p>
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className={selectedCert.complianceScore >= 90 ? 'text-risk-low' : selectedCert.complianceScore >= 75 ? 'text-risk-medium' : 'text-risk-high'} />
                      <p className={`text-sm font-bold ${selectedCert.complianceScore >= 90 ? 'text-risk-low' : selectedCert.complianceScore >= 75 ? 'text-risk-medium' : 'text-risk-high'}`}>
                        {selectedCert.complianceScore}/100
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-3 bg-bg rounded-lg">
              <p className="text-xs text-text-muted mb-1">Certifying Body</p>
              <p className="text-sm font-medium text-text">{selectedCert.body}</p>
            </div>

            {selectedCert.scope && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Scope</p>
                <p className="text-sm text-text leading-relaxed">{selectedCert.scope}</p>
              </div>
            )}

            {/* Next audit callout */}
            {selectedCert.nextAuditDate && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-teal/5 border border-teal/20">
                <Award size={14} className="text-teal flex-shrink-0" />
                <p className="text-xs text-teal">
                  Next surveillance audit: <span className="font-semibold">{format(new Date(selectedCert.nextAuditDate), 'd MMM yyyy')}</span>
                </p>
              </div>
            )}

            {/* ALCOA Attribution */}
            {selectedCert.issuedBy && (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                <div className="p-2.5 bg-bg rounded-lg">
                  <p className="text-[10px] text-text-muted uppercase tracking-wide mb-1 font-semibold">Issued By</p>
                  <p className="text-xs font-semibold text-text">{selectedCert.issuedBy}</p>
                </div>
                <div className="p-2.5 bg-bg rounded-lg">
                  <p className="text-[10px] text-text-muted uppercase tracking-wide mb-1 font-semibold">Verified By</p>
                  <p className="text-xs font-semibold text-text">{selectedCert.verifiedBy || '—'}</p>
                </div>
              </div>
            )}

            {/* Audit History */}
            {selectedCert.auditHistory && selectedCert.auditHistory.length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 mb-3">
                  <History size={13} className="text-teal" />
                  <p className="text-xs font-semibold text-text">Audit History</p>
                </div>
                <div className="space-y-2">
                  {selectedCert.auditHistory.map((a, i) => (
                    <div key={i} className="p-2.5 bg-bg rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold text-text">{a.type}</p>
                          <p className="text-[10px] text-text-muted mt-0.5">Auditor: {a.auditor} · {format(new Date(a.date), 'd MMM yyyy')}</p>
                        </div>
                        <span className={`text-xs font-bold flex-shrink-0 ${a.score >= 90 ? 'text-risk-low' : a.score >= 75 ? 'text-risk-medium' : 'text-risk-high'}`}>
                          {a.score}/100
                        </span>
                      </div>
                      {a.finding && <p className="text-[10px] text-text-muted mt-1 italic">{a.finding}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Renewal History */}
            {selectedCert.renewalHistory && selectedCert.renewalHistory.length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 mb-3">
                  <RotateCcw size={13} className="text-teal" />
                  <p className="text-xs font-semibold text-text">Renewal History</p>
                  <span className="text-[10px] text-text-dim ml-1">({selectedCert.renewalCount} renewal{selectedCert.renewalCount !== 1 ? 's' : ''})</span>
                </div>
                <div className="space-y-2">
                  {selectedCert.renewalHistory.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-bg rounded-lg">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0 mt-1.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-text">{r.renewedBy}</p>
                          <span className="text-[10px] text-text-dim flex-shrink-0">{format(new Date(r.date), 'd MMM yyyy')}</span>
                        </div>
                        {r.notes && <p className="text-[10px] text-text-muted mt-0.5">{r.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Start Renewal button in detail view */}
            {(selectedCert.status === 'renewal_due' || selectedCert.status === 'expired') && (
              <div className="pt-2 border-t border-border">
                <button
                  onClick={() => { setSelectedCert(null); setShowRenew(selectedCert.id); }}
                  className="btn-primary w-full"
                >
                  <RotateCcw size={13} /> Start Renewal
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Renewal Workflow Modal */}
      <Modal
        isOpen={!!showRenew}
        onClose={() => { setShowRenew(null); setRenewForm({ renewalDate: '', renewedBy: '', renewalCost: '', notifyDays: '14', notes: '' }); }}
        title="Start Renewal"
        subtitle={renewingCert ? `Renewing: ${renewingCert.name}` : 'Certification Renewal'}
        size="md"
        footer={
          <>
            <button
              onClick={() => { setShowRenew(null); setRenewForm({ renewalDate: '', renewedBy: '', renewalCost: '', notifyDays: '14', notes: '' }); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => { if (showRenew) renewMutation.mutate({ id: showRenew, payload: renewForm }); }}
              disabled={renewMutation.isPending || !renewForm.renewalDate || !renewForm.renewedBy}
              className="btn-primary"
            >
              {renewMutation.isPending ? 'Confirming...' : 'Confirm Renewal'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {renewingCert && (
            <div className="p-3 rounded-lg bg-risk-medium/5 border border-risk-medium/20">
              <p className="text-xs text-text-muted">Certification</p>
              <p className="text-sm font-semibold text-text mt-0.5">{renewingCert.name}</p>
              <p className="text-xs text-text-muted mt-0.5">{renewingCert.certCode} · {renewingCert.lab?.name}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Renewal Date *</label>
              <input
                type="date"
                className="input"
                value={renewForm.renewalDate}
                onChange={(e) => setRenewForm((f) => ({ ...f, renewalDate: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Renewed By *</label>
              <input
                className="input"
                placeholder="Responsible person..."
                value={renewForm.renewedBy}
                onChange={(e) => setRenewForm((f) => ({ ...f, renewedBy: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Renewal Cost (optional)</label>
              <input
                type="number"
                className="input"
                placeholder="0.00"
                value={renewForm.renewalCost}
                onChange={(e) => setRenewForm((f) => ({ ...f, renewalCost: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Reminder</label>
              <select
                className="select"
                value={renewForm.notifyDays}
                onChange={(e) => setRenewForm((f) => ({ ...f, notifyDays: e.target.value }))}
              >
                <option value="7">Remind 7 days before</option>
                <option value="14">Remind 14 days before</option>
                <option value="30">Remind 30 days before</option>
                <option value="60">Remind 60 days before</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input resize-none"
              rows={3}
              placeholder="Additional renewal notes..."
              value={renewForm.notes}
              onChange={(e) => setRenewForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      {/* Batch Upload Modal */}
      <Modal
        isOpen={showBatchUpload}
        onClose={() => { setShowBatchUpload(false); setBatchFiles(null); setBatchLabId(''); setBatchCertType(''); }}
        title="Batch Certificate Upload"
        subtitle="Upload multiple certificate PDFs at once"
        size="md"
        footer={
          <>
            <button
              onClick={() => { setShowBatchUpload(false); setBatchFiles(null); setBatchLabId(''); setBatchCertType(''); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => batchUploadMutation.mutate()}
              disabled={batchUploadMutation.isPending || !batchFiles || batchFiles.length === 0 || !batchLabId || !batchCertType}
              className="btn-primary"
            >
              {batchUploadMutation.isPending ? 'Uploading...' : `Upload ${batchFiles ? batchFiles.length : 0} File(s)`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Certificate Files (PDF) *</label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                id="batch-file-input"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 10) {
                    alert('Maximum 10 files allowed per batch upload.');
                    e.target.value = '';
                    return;
                  }
                  setBatchFiles(e.target.files);
                }}
              />
              <label htmlFor="batch-file-input" className="cursor-pointer">
                <Upload size={24} className="mx-auto text-text-dim mb-2" />
                <p className="text-sm text-text-muted">Click to select PDF files</p>
                <p className="text-xs text-text-dim mt-1">Up to 10 files · PDF only</p>
              </label>
              {batchFiles && batchFiles.length > 0 && (
                <div className="mt-3 space-y-1">
                  {Array.from(batchFiles).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-text bg-bg rounded px-2 py-1">
                      <span className="text-teal">•</span>
                      <span className="truncate">{f.name}</span>
                      <span className="text-text-dim ml-auto flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="label">Lab *</label>
            <select className="select" value={batchLabId} onChange={(e) => setBatchLabId(e.target.value)}>
              <option value="">Select lab...</option>
              {(labs || []).map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Certification Type *</label>
            <select className="select" value={batchCertType} onChange={(e) => setBatchCertType(e.target.value)}>
              <option value="">Select type...</option>
              {['ISO', 'GMP', 'NABL', 'WHO', 'FDA', 'Other'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-text-dim">
            Filenames will be automatically mapped to existing certification records where possible.
          </p>
        </div>
      </Modal>
    </div>
  );
}
